import React from 'react';
import ReactModal from 'react-modal';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { NotificationManager } from 'react-notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faUser, faShieldHalved, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { isPinAuthenticated } from '../utils/pinVerification';

if (typeof document !== 'undefined') ReactModal.setAppElement('#root');

function MyModalComponent2(props) {
  const [pseudo, setPseudo] = React.useState(props.user?.pseudo || '');
  const [idAvatar, setIdAvatar] = React.useState(props.user?.id_avatar || 0);
  const [pin, setPin] = React.useState(['', '', '', '']);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const pinRefs = React.useRef([]);
  const focusTimer = React.useRef(null);
  const isMounted = React.useRef(true);
  const dispatch = useDispatch();
  const user = useSelector(state => state.userGlobal.user);

  const onModalClose = () => {
    setPin(['', '', '', '']);
    if (typeof document !== 'undefined') document.body.classList.remove('ae-user-login-modal-open');
    props.onCloseModal(false, 2);
  };

  React.useEffect(() => {
    setPseudo(props.user?.pseudo || '');
    setIdAvatar(props.user?.id_avatar || 0);
  }, [props.user?.pseudo, props.user?.id_avatar]);

  React.useEffect(() => {
    if (!props.IsModalOpened && typeof document !== 'undefined') {
      document.body.classList.remove('ae-user-login-modal-open');
    }
    return () => {
      if (props.IsModalOpened && typeof document !== 'undefined') {
        document.body.classList.remove('ae-user-login-modal-open');
      }
    };
  }, [props.IsModalOpened]);

  React.useEffect(() => () => {
    isMounted.current = false;
    if (focusTimer.current) window.clearTimeout(focusTimer.current);
    if (typeof document !== 'undefined') document.body.classList.remove('ae-user-login-modal-open');
  }, []);

  const focusFirstPin = () => {
    if (focusTimer.current) window.clearTimeout(focusTimer.current);
    focusTimer.current = window.setTimeout(() => {
      if (isMounted.current && pinRefs.current[0]) pinRefs.current[0].focus();
    }, 80);
  };

  const updatePin = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, '').slice(-1);
    setPin((current) => current.map((digit, digitIndex) => digitIndex === index ? value : digit));
    if (value && index < 3 && pinRefs.current[index + 1]) pinRefs.current[index + 1].focus();
  };

  const handlePinKeyDown = (event, index) => {
    if (event.key === 'Backspace' && !pin[index] && index > 0) {
      pinRefs.current[index - 1].focus();
    }
    if (event.key === 'ArrowLeft' && index > 0) pinRefs.current[index - 1].focus();
    if (event.key === 'ArrowRight' && index < 3) pinRefs.current[index + 1].focus();
    if (event.key === 'Enter') handleSubmit(event);
  };

  const handlePinPaste = (event) => {
    const digits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4).split('');
    if (!digits.length) return;
    event.preventDefault();
    setPin([0, 1, 2, 3].map((index) => digits[index] || ''));
    pinRefs.current[Math.min(digits.length, 4) - 1].focus();
  };

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    const codePin = pin.join('');
    if (codePin.length !== 4 || isSubmitting) {
      if (codePin.length !== 4) NotificationManager.warning('Saisissez votre code secret à 4 chiffres', '', 3000);
      return;
    }
    saveModif(codePin);
  };

  const saveModif = (codePin) => {
    const formData = new FormData();
    formData.append('id_compte', user.id_compte);
    formData.append('id_user', props.user.id_user);
    formData.append('pin_code', codePin);
    setIsSubmitting(true);

    axios.post('https://eps.acces-editions.com/api/matchPin', formData)
      .then((res) => {
        if (isPinAuthenticated(res.data, codePin)) {
          NotificationManager.success('Vous êtes connecté', '', 3000);
          dispatch({ type: 'user/userConnected', payload: { id_user: props.user.id_user, connected: true, id_avatar: props.user.id_avatar, pseudo: props.user.pseudo } });
          dispatch({ type: 'user/classeDetail', payload: [] });
          dispatch({ type: 'user/userDeviceClear' });
          onModalClose();

          axios.get(`https://eps.acces-editions.com/api/getMyClasse?id_compte=${user.id_compte}&id_user=${props.user.id_user}`)
            .then((classeResponse) => {
              const classes = Array.isArray(classeResponse.data) ? classeResponse.data : [];
              dispatch({ type: 'user/classeDetail', payload: classes });
            })
            .catch(() => dispatch({ type: 'user/classeDetail', payload: [] }));

        } else {
          if (!isMounted.current) return;
          setPin(['', '', '', '']);
          focusFirstPin();
          NotificationManager.warning('Code secret invalide', '', 3000);
        }
      })
      .catch(() => NotificationManager.error('La connexion a échoué. Veuillez réessayer.', '', 3000))
      .finally(() => {
        if (isMounted.current) setIsSubmitting(false);
      });
  };

  return (
    <ReactModal
      isOpen={props.IsModalOpened}
      onAfterOpen={focusFirstPin}
      onRequestClose={onModalClose}
      overlayClassName="ae-user-login-overlay"
      className="ae-user-login-modal"
      bodyOpenClassName="ae-user-login-modal-open"
      contentLabel={`Connexion de ${pseudo}`}
      shouldCloseOnOverlayClick
    >
      <div className="ae-user-login-ring ae-user-login-ring--violet" />

      <button type="button" className="ae-user-login-close" onClick={onModalClose} aria-label="Fermer">
        <FontAwesomeIcon icon={faXmark} />
      </button>

      <form onSubmit={handleSubmit}>
        <div className="ae-user-login-body">
          <h2>Connexion</h2>

          <div className="ae-user-login-name">
            <FontAwesomeIcon icon={faUser} />
            <strong>{pseudo}</strong>
          </div>

          <div className="ae-user-login-avatar-wrap">
            <img
              src={`${process.env.PUBLIC_URL}/img/avatars/avatar_${idAvatar}.png`}
              alt={`Avatar de ${pseudo}`}
            />
          </div>

          <div className="ae-user-login-instruction">
            <FontAwesomeIcon icon={faShieldHalved} />
            <span>Validez l'accès avec votre code secret à 4 chiffres</span>
          </div>

          <div className="ae-user-login-pin" onPaste={handlePinPaste}>
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={(element) => { pinRefs.current[index] = element; }}
                type="password"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength="1"
                value={digit}
                placeholder="•"
                onChange={(event) => updatePin(index, event.target.value)}
                onKeyDown={(event) => handlePinKeyDown(event, index)}
                aria-label={`Chiffre ${index + 1} du code secret`}
              />
            ))}
          </div>
        </div>

        <footer className="ae-user-login-footer">
          <div className="ae-user-login-ring ae-user-login-ring--green" />
          <button type="button" className="ae-user-login-cancel" onClick={onModalClose}>Fermer</button>
          <button type="submit" className="ae-user-login-submit" disabled={isSubmitting}>
            <span>{isSubmitting ? 'Connexion…' : 'Connexion'}</span>
            {!isSubmitting && <FontAwesomeIcon icon={faArrowRight} />}
          </button>
        </footer>
      </form>
    </ReactModal>
  );
}

export default MyModalComponent2;
